import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
  keepPreviousData,
} from '@tanstack/react-query';

import {
  LinkedServiceData,
  OtherServiceData,
  ServiceAccordianData,
  ServiceListResponse,
} from '../@types';
import {
  ActionType,
  BillingType,
  BulkEditServiceDataType,
  EstimatedTimeOfArrivalQuery,
  ProcessType,
  ServiceAttribute,
  ServiceAttendanceObject,
  ServiceBillingInfo,
  ServiceCommonType,
  ServiceCreationObject,
  ServiceNodeAllocationData,
  ServiceObject,
  ServicesNodesAttributes,
  ServiceUpdateRequest,
  OtherServiceListData,
} from '../@types/service.type';
import {
  BulkServiceIdsRequest,
  createService,
  createServiceBulk,
  getActionTypes,
  getAllServiceAttendance,
  getAllServices,
  getBillingInfo,
  getBillingTypes,
  getBulkServicesByIds,
  getEstimatedTimeOfArrival,
  getLinkedServices,
  getOtherServicesUnderMainLocation,
  getProcessTypes,
  getServiceActionProcessTypes,
  getServiceById,
  getServicesNodesLocationRelatedAttributes,
  getServicesUnderNodeId,
  ParentServiceUpdateRequest,
  ServiceBulkCreateRequest,
  updateBillingInfo,
  updateBulkServiceEditList,
  updateBulkServiceList,
  updateParentService,
  updateReportedInformationByServiceId,
  ReportedInfoUpdateRequest,
  updateServiceById,
  updateServiceNodeAllocation,
  createOrUpdateServiceAttributes,
  ServiceQueryOptions,
} from '../api/services';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';
import {
  AppActionTypesKey,
  AppLinkedServicesKey,
  AppProcessTypesKey,
  AppServiceActionProcessTypesKey,
  AppServicesKey,
  GetServiceKey,
  OtherServicesKey,
  ServiceAttendanceKey,
  ServiceBulkUpdateKey,
  ServiceAccordianKey,
  GetEstimatedTimeOfArrivalKey,
  ServicesNodesAttributesKey,
  ServiceBillingTypesKey,
  ServiceLogAllKey,
  ServiceBillingInfoKey,
} from '../configs/services';
import { NodesAllocationByService } from '../configs/structures';

export const useAddService = (): UseMutationResult<ServiceObject, Error, ServiceCreationObject> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceCreationObject) => {
      const response = await createService(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey],
      });
    },
  });
};

export const useGetActionTypes = (): UseQueryResult<ActionType[]> => {
  return useQuery({
    queryKey: [AppActionTypesKey],
    queryFn: () => getActionTypes(),
    staleTime: 2 * 60 * 60 * 1000,
  });
};

export const useGetProcessTypes = (): UseQueryResult<ProcessType[]> => {
  return useQuery({
    queryKey: [AppProcessTypesKey],
    queryFn: () => getProcessTypes(),
    staleTime: 2 * 60 * 60 * 1000,
  });
};

export const useGetServiceActionProcessTypes = (
  activeOnly?: boolean
): UseQueryResult<ServiceCommonType> => {
  return useQuery({
    queryKey: [AppServiceActionProcessTypesKey],
    queryFn: () => getServiceActionProcessTypes(activeOnly),
    staleTime: 2 * 60 * 60 * 1000,
  });
};

export const useGetServicePaginatedData = (
  queryOption: ServiceQueryOptions,
  columnSelectionItems: ColumnSelectionItem[]
): UseQueryResult<ServiceListResponse> => {
  return useQuery({
    queryKey: [AppServicesKey, queryOption, columnSelectionItems],
    queryFn: () => getAllServices(queryOption, columnSelectionItems),
    gcTime: 0,
    enabled: false,
  });
};

export const useGetMobileServiceList = (
  queryOption: ServiceQueryOptions,
  columnSelectionItems: ColumnSelectionItem[]
): UseQueryResult<ServiceListResponse> => {
  return useQuery({
    queryKey: [AppServicesKey, 'mobile', queryOption, columnSelectionItems],
    queryFn: () => getAllServices(queryOption, columnSelectionItems),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnReconnect: true,
  });
};

export const useGetMobileServiceListInfinite = (
  queryOption: Omit<ServiceQueryOptions, 'page'>,
  columnSelectionItems: ColumnSelectionItem[]
): UseInfiniteQueryResult<InfiniteData<ServiceListResponse>, Error> => {
  return useInfiniteQuery({
    queryKey: [AppServicesKey, 'mobile-infinite', queryOption, columnSelectionItems],
    queryFn: ({ pageParam = 0 }) =>
      getAllServices({ ...queryOption, page: pageParam }, columnSelectionItems),
    initialPageParam: 0,
    getNextPageParam: (lastPage: ServiceListResponse, _allPages, lastPageParam: number) => {
      const pageSize = lastPage.limit ?? queryOption.pageSize ?? 20;
      if (!lastPage.data?.length || lastPage.data.length < pageSize) return undefined;
      return lastPageParam + 1;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnReconnect: true,
  });
};

interface ServiceUpdateData extends ServiceUpdateRequest {
  id: number;
}

export const useUpdateService = (
  id: number
): UseMutationResult<ServiceObject, Error, ServiceUpdateData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ServiceUpdateData) => updateServiceById(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [NodesAllocationByService, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id],
      });
    },
  });
};

export const useGetServiceById = (id: number): UseQueryResult<ServiceObject> => {
  return useQuery({
    queryKey: [GetServiceKey, id],
    queryFn: () => getServiceById(id),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!id,
  });
};

interface ReportedInfoUpdateData extends ReportedInfoUpdateRequest {
  id: number;
}

export const useUpdateReportedInformation = (): UseMutationResult<
  ServiceObject,
  Error,
  ReportedInfoUpdateData
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReportedInfoUpdateData) =>
      updateReportedInformationByServiceId(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey],
      });
    },
  });
};

export const useUpdateBulkServiceList = (): UseMutationResult<
  void,
  Error,
  BulkEditServiceDataType
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkEditServiceDataType) => await updateBulkServiceList(data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceBulkUpdateKey],
      });
    },
  });
};

export const useUpdateBulkServiceEditList = (): UseMutationResult<
  void,
  Error,
  BulkEditServiceDataType
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkEditServiceDataType) => await updateBulkServiceEditList(data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceBulkUpdateKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey],
      });
    },
  });
};

export const useGetBulkServicesByIds = (): UseMutationResult<
  ServiceObject[],
  Error,
  BulkServiceIdsRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkServiceIdsRequest) => {
      const response = await getBulkServicesByIds(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceBulkUpdateKey],
      });
    },
  });
};

export const useGetOtherServicesUnderMainLocation = (
  nodeId: number,
  queryOption: { page: number; pageSize: number }
): UseQueryResult<OtherServiceListData> => {
  return useQuery({
    queryKey: [OtherServicesKey, nodeId, queryOption],
    queryFn: async () => getOtherServicesUnderMainLocation(nodeId, queryOption),
    gcTime: 0,
    enabled: nodeId !== 0,
  });
};

export const useGetLinkedServices = (serviceId: number): UseQueryResult<LinkedServiceData> => {
  return useQuery({
    queryKey: [AppLinkedServicesKey, serviceId],
    queryFn: () => getLinkedServices(serviceId),
    enabled: !!serviceId,
  });
};

interface ParentServiceUpdateData extends ParentServiceUpdateRequest {
  id: number;
  isChild?: boolean;
}

export const useUpdateParentService = (
  serviceId: number
): UseMutationResult<ServiceObject, Error, ParentServiceUpdateData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ParentServiceUpdateData) => updateParentService(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [AppLinkedServicesKey, serviceId],
      });
    },
  });
};

export const useGetAllServiceAttendance = (
  serviceId: number
): UseQueryResult<ServiceAttendanceObject[]> => {
  return useQuery({
    queryKey: [ServiceAttendanceKey, serviceId],
    queryFn: () => getAllServiceAttendance(serviceId),
  });
};

export const useUpdateBillingInfo = (
  serviceId: number
): UseMutationResult<void, Error, ServiceBillingInfo> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ServiceBillingInfo) => updateBillingInfo(serviceId, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, serviceId],
      });
    },
  });
};

export const useGetServicesUnderNodeId = (
  nodeId: number,
  includeChildRecords: boolean
): UseQueryResult<ServiceAccordianData[]> => {
  return useQuery({
    queryKey: [ServiceAccordianKey, nodeId],
    queryFn: () => getServicesUnderNodeId(nodeId, includeChildRecords),
  });
};

export const useUpdateServiceAllocation = (
  id: number
): UseMutationResult<void, Error, ServiceNodeAllocationData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceNodeAllocationData) => {
      return await updateServiceNodeAllocation(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id],
      });
    },
  });
};

export const useGetEstimatedTimeOfArrival = (
  queryOption: EstimatedTimeOfArrivalQuery
): UseQueryResult<string> => {
  return useQuery({
    queryKey: [GetEstimatedTimeOfArrivalKey],
    queryFn: () => getEstimatedTimeOfArrival(queryOption),
    enabled: false,
  });
};

export const useGetServicesNodesLocationRelatedAttributes = (
  serviceId: number,
  mainLocationNodeId: number,
  subLocationNodeId: number
): UseQueryResult<ServicesNodesAttributes> => {
  return useQuery({
    queryKey: [ServicesNodesAttributesKey, serviceId],
    queryFn: () =>
      getServicesNodesLocationRelatedAttributes(serviceId, mainLocationNodeId, subLocationNodeId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: false,
  });
};

interface ServiceAttributeWithServiceId extends ServiceAttribute {
  serviceId: string | number;
  attributeTypeCode?: string;
  name?: string;
  value?: unknown;
}

export const useCreateOrUpdateServiceAttributes = (): UseMutationResult<
  void,
  Error,
  ServiceAttributeWithServiceId
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceAttributeWithServiceId) => {
      return await createOrUpdateServiceAttributes(data);
    },

    onSuccess: async (mutationResponse, variables) => {
      const serviceIdNum =
        typeof variables?.serviceId === 'string'
          ? parseInt(variables.serviceId)
          : variables?.serviceId;
      await queryClient.invalidateQueries({
        queryKey: [ServicesNodesAttributesKey, serviceIdNum],
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, serviceIdNum],
      });
      await queryClient.invalidateQueries({
        queryKey: [NodesAllocationByService, serviceIdNum],
      });
    },
  });
};

export const useGetServiceBillingTypes = (): UseQueryResult<BillingType[]> => {
  return useQuery({
    queryKey: [ServiceBillingTypesKey],
    queryFn: () => getBillingTypes(),
  });
};

export const useGetBillingInfo = (serviceId: number): UseQueryResult<ServiceBillingInfo> => {
  return useQuery({
    queryKey: [ServiceBillingInfoKey, serviceId],
    queryFn: () => getBillingInfo(serviceId),
    enabled: false,
  });
};

export const useAddServiceBulk = (): UseMutationResult<
  ServiceObject[],
  Error,
  ServiceBulkCreateRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceBulkCreateRequest) => {
      const response = await createServiceBulk(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey],
      });
    },
  });
};
